import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Receipt, Gift, RefreshCcw } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { eur, fmtDate, fmtDateTime, Loading, EmptyState, PaymentBadge } from "@/components/common";
import Section from "@/components/Section";
import BackNav from "@/components/BackNav";
import AdminDashboard from "./AdminDashboard";
import AdminInterventi from "./AdminInterventi";
import AdminCatalogo from "./AdminCatalogo";
import AdminPersone from "./AdminPersone";
import AdminIscrizioni from "./AdminIscrizioni";
import AdminGiri from "./AdminGiri";
import AdminRicorrenze from "./AdminRicorrenze";
import AdminRichiesteCimiteri from "./AdminRichiesteCimiteri";
import AdminRicavi from "./AdminRicavi";

const TABS = [
  ["dashboard", "Dashboard"],
  ["iscrizioni", "Nuove iscrizioni"],
  ["interventi", "Interventi"],
  ["giri", "Giri di sabato"],
  ["ricorrenze", "Ricorrenze"],
  ["cimiteri_req", "Richieste cimiteri"],
  ["persone", "Clienti & Cimiteri"],
  ["catalogo", "Fiori, Prezzi & Offerte"],
  ["contabilita", "Pagamenti & Crediti"],
];

const SUB_LABELS = { attiva: "Attivo", in_attesa_attivazione: "In attesa attivazione", sospesa: "Sospeso", annullata: "Annullato" };

function Contabilita() {
  const [payments, setPayments] = useState(null);
  const [credits, setCredits] = useState([]);
  const [subs, setSubs] = useState([]);
  const [editSub, setEditSub] = useState(null);

  const loadSubs = () => api.get("/admin/subscriptions").then((r) => setSubs(r.data));
  const loadPayments = () => api.get("/admin/payments").then((r) => setPayments(r.data));

  const confirmPay = async (id) => {
    try {
      await api.post(`/admin/payments/${id}/confirm`);
      toast.success("Pagamento confermato: ordine attivato e interventi assegnati al professionista.");
      loadPayments();
      loadSubs();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const cancelPay = async (id) => {
    try {
      await api.post(`/admin/payments/${id}/cancel`);
      toast.success("Pagamento annullato: gli interventi futuri sono stati disattivati.");
      loadPayments();
      loadSubs();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveSub = async (id) => {
    try {
      await api.patch(`/admin/subscriptions/${id}`, {
        start_date: editSub.start_date || undefined,
        duration_months: editSub.duration_months,
        interventions_per_month: editSub.interventions_per_month,
        status: editSub.status,
      });
      toast.success("Abbonamento aggiornato: le uscite future sono state ricalcolate.");
      setEditSub(null);
      loadSubs();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => {
    loadPayments();
    api.get("/admin/credits").then((r) => setCredits(r.data));
    loadSubs();
  }, []);

  if (!payments) return <Loading />;

  const isPending = (p) => p.status === "in_attesa" || p.status === "in_verifica";
  const pendingPayments = payments.filter(isPending);
  const otherPayments = payments.filter((p) => !isPending(p));

  // Azioni: nella tabella desktop conservano i data-testid originali;
  // nelle card mobile usano il suffisso "-m" per non duplicare gli id.
  const payActions = (p, m) => (
    <>
      {(p.status === "in_attesa" || p.status === "in_verifica") && (
        <button data-testid={`admin-pay-confirm${m}-${p.id}`}
          onClick={() => {
            const msg = p.method === "bonifico"
              ? "Confermi di aver ricevuto il pagamento tramite bonifico?"
              : p.method === "contanti"
                ? "Confermi di aver ricevuto il pagamento in contanti?"
                : "Confermi il pagamento?";
            if (window.confirm(msg)) confirmPay(p.id);
          }}
          className="text-xs font-bold text-white bg-[#2f5d3e] hover:bg-[#254a32] px-3 py-2 rounded-full transition-colors duration-150 whitespace-nowrap min-h-[36px]">
          Segna come pagato
        </button>
      )}
      {(p.status === "in_attesa" || p.status === "in_verifica" || p.status === "completato") && (
        <button data-testid={`admin-pay-cancel${m}-${p.id}`} onClick={() => cancelPay(p.id)}
          className="text-xs font-semibold text-red-800 hover:underline whitespace-nowrap min-h-[36px]">
          Annulla pagamento
        </button>
      )}
    </>
  );

  const payCard = (p) => (
    <div key={p.id} className="card-soft p-4 space-y-2" data-testid={`admin-payment-m-${p.id}`}>
      <div className="flex justify-between items-start gap-3">
        <div className="min-w-0">
          <p className="text-navy font-medium truncate">{p.customer_name}</p>
          <p className="text-xs text-muted-soft truncate">{p.customer_email}{p.customer_phone ? ` · ${p.customer_phone}` : ""}</p>
        </div>
        <PaymentBadge status={p.status} method={p.method} />
      </div>
      <p className="text-sm text-body">
        {p.servizio || p.kind}
        {p.subscription_status ? ` · Abb.: ${SUB_LABELS[p.subscription_status] || p.subscription_status}` : ""}
      </p>
      <p className="text-xs text-muted-soft">
        {fmtDate(p.created_at)} · <span className="capitalize">{p.method}</span>{p.simulated ? " (simulato)" : ""} · {p.comune || "—"} · {p.professional_name || "—"} · ID {p.id.slice(0, 8)}
        {p.confirmed_at ? ` · Conf.: ${fmtDateTime(p.confirmed_at)}` : ""}
      </p>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className="font-semibold text-navy">{eur(p.amount)}</span>
        <div className="flex gap-3 items-center flex-wrap">{payActions(p, "-m")}</div>
      </div>
    </div>
  );

  const payTable = (rows) => (
    <div className="card-soft overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-muted-soft border-b border-[#e2e8f0]">
            <th className="p-4">Data</th><th className="p-4">Cliente</th><th className="p-4">Servizio</th><th className="p-4">Metodo</th><th className="p-4">Importo</th><th className="p-4">Comune</th><th className="p-4">Professionista</th><th className="p-4">Stato</th><th className="p-4">Azioni</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.id} className="border-b border-[#f1f5f9] last:border-0" data-testid={`admin-payment-${p.id}`}>
              <td className="p-4 whitespace-nowrap">{fmtDate(p.created_at)}{p.confirmed_at ? <p className="text-xs text-muted-soft mt-0.5">Conf.: {fmtDateTime(p.confirmed_at)}</p> : null}</td>
              <td className="p-4">
                <p className="text-navy font-medium">{p.customer_name}</p>
                <p className="text-xs text-muted-soft">{p.customer_email}{p.customer_phone ? ` · ${p.customer_phone}` : ""}</p>
              </td>
              <td className="p-4">
                <p>{p.servizio || p.kind}</p>
                {p.subscription_status && <p className="text-xs text-muted-soft">Abb.: {SUB_LABELS[p.subscription_status] || p.subscription_status}</p>}
                <p className="text-xs text-muted-soft">ID: {p.id.slice(0, 8)}</p>
              </td>
              <td className="p-4 capitalize">{p.method}{p.simulated ? " (simulato)" : ""}</td>
              <td className="p-4 font-semibold text-navy whitespace-nowrap">{eur(p.amount)}</td>
              <td className="p-4">{p.comune || "—"}</td>
              <td className="p-4">{p.professional_name || "—"}</td>
              <td className="p-4"><PaymentBadge status={p.status} method={p.method} /></td>
              <td className="p-4">
                <div className="flex flex-col gap-1.5 items-start">{payActions(p, "")}</div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="admin-contabilita">
      <AdminRicavi />
      <section>
        <h2 className="font-serif text-2xl text-navy mb-4">Pagamenti da confermare</h2>
        {pendingPayments.length === 0 ? <EmptyState title="Nessun pagamento da confermare" /> : (
          <>
            <div className="space-y-3 lg:hidden">{pendingPayments.map(payCard)}</div>
            <div className="hidden lg:block">{payTable(pendingPayments)}</div>
          </>
        )}
      </section>

      <Section title="Storico pagamenti" count={otherPayments.length} icon={Receipt} testid="admin-payments-history">
        {otherPayments.length === 0 ? <EmptyState title="Nessun altro pagamento" /> : (
          <>
            <div className="space-y-3 lg:hidden">{otherPayments.map(payCard)}</div>
            <div className="hidden lg:block">{payTable(otherPayments)}</div>
          </>
        )}
      </Section>

      <Section title="Bonus prima uscita" count={credits.length} icon={Gift} testid="admin-bonus-section">
        {credits.length === 0 ? <EmptyState title="Nessun bonus" /> : (
          <div className="card-soft overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-soft border-b border-[#e2e8f0]">
                  <th className="p-4">Cliente</th><th className="p-4">Importo</th><th className="p-4">Stato</th><th className="p-4">Acquistato il</th><th className="p-4">Scadenza</th><th className="p-4">Utilizzo</th>
                </tr>
              </thead>
              <tbody>
                {credits.map((c) => (
                  <tr key={c.id} className="border-b border-[#f1f5f9] last:border-0" data-testid={`admin-credit-${c.id}`}>
                    <td className="p-4 text-navy font-medium">{c.customer_name}</td>
                    <td className="p-4">{eur(c.amount)}</td>
                    <td className="p-4">
                      {c.status === "disponibile" ? <span className="text-[#2f5d3e] font-semibold">Disponibile</span>
                        : c.status === "utilizzato" ? <span className="text-[#1e3a5f] font-semibold">Utilizzato</span>
                        : <span className="text-muted-soft">Scaduto</span>}
                    </td>
                    <td className="p-4">{fmtDate(c.created_at)}</td>
                    <td className="p-4">{fmtDate(c.expires_at)}</td>
                    <td className="p-4 text-xs text-muted-soft">{c.used_for ? `${c.used_for}${c.used_at ? ` · ${fmtDate(c.used_at)}` : ""}` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title="Abbonamenti" count={subs.length} icon={RefreshCcw} testid="admin-subs-section" id="admin-subscriptions" defaultOpen>
        {subs.length === 0 ? <EmptyState title="Nessun abbonamento" /> : (
          <div className="card-soft overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-soft border-b border-[#e2e8f0]">
                  <th className="p-4">Cliente</th><th className="p-4">Piano</th><th className="p-4">Interventi</th><th className="p-4">Pagato</th><th className="p-4">Fine</th><th className="p-4">Stato</th><th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {subs.map((s) => (
                  editSub?.id === s.id ? (
                    <tr key={s.id} className="border-b border-[#f1f5f9] last:border-0 bg-[#f8fafc]" data-testid={`admin-sub-edit-${s.id}`}>
                      <td className="p-4 text-navy font-medium">{s.customer_name}</td>
                      <td className="p-4" colSpan={5}>
                        <div className="flex flex-wrap items-end gap-3">
                          <div>
                            <label className="label-soft">Data inizio</label>
                            <input type="date" className="input-soft !w-auto !py-1.5 text-sm" data-testid="sub-edit-start"
                              value={editSub.start_date} onChange={(e) => setEditSub({ ...editSub, start_date: e.target.value })} />
                          </div>
                          <div>
                            <label className="label-soft">Durata (mesi)</label>
                            <input type="number" min="1" max="12" className="input-soft !w-20 !py-1.5 text-sm" data-testid="sub-edit-months"
                              value={editSub.duration_months} onChange={(e) => setEditSub({ ...editSub, duration_months: parseInt(e.target.value, 10) || 1 })} />
                          </div>
                          <div>
                            <label className="label-soft">Interventi/mese</label>
                            <input type="number" min="0.25" step="0.25" className="input-soft !w-24 !py-1.5 text-sm" data-testid="sub-edit-freq"
                              value={editSub.interventions_per_month} onChange={(e) => setEditSub({ ...editSub, interventions_per_month: parseFloat(e.target.value) || 1 })} />
                          </div>
                          <div>
                            <label className="label-soft">Stato</label>
                            <select className="input-soft !w-auto !py-1.5 text-sm" data-testid="sub-edit-status"
                              value={editSub.status} onChange={(e) => setEditSub({ ...editSub, status: e.target.value })}>
                              <option value="attiva">Attivo</option>
                              <option value="sospesa">Sospeso</option>
                              <option value="annullata">Annullato</option>
                            </select>
                          </div>
                          <button data-testid="sub-edit-save" onClick={() => saveSub(s.id)} className="btn-primary !py-2 !px-4 text-sm min-h-[44px]">Salva</button>
                          <button onClick={() => setEditSub(null)} className="text-xs text-muted-soft hover:underline min-h-[44px]">Annulla</button>
                        </div>
                        <p className="text-xs text-muted-soft mt-2">Le uscite future verranno rigenerate automaticamente; quelle completate restano conservate nello storico.</p>
                      </td>
                    </tr>
                  ) : (
                    <tr key={s.id} className="border-b border-[#f1f5f9] last:border-0" data-testid={`admin-sub-${s.id}`}>
                      <td className="p-4 text-navy font-medium">{s.customer_name}</td>
                      <td className="p-4">
                        {s.title}
                        {s.extras?.length > 0 && (
                          <p className="text-xs text-muted-soft mt-0.5" data-testid={`sub-extras-${s.id}`}>
                            Ricorrenze: {s.extras.map((e) => `${e.name} (${eur(e.price)})`).join(" · ")}
                          </p>
                        )}
                      </td>
                      <td className="p-4">{s.interventions_used}/{s.interventions_total}</td>
                      <td className="p-4">{eur(s.amount_paid)}{s.credit_applied > 0 ? ` (−${eur(s.credit_applied)} bonus)` : ""}</td>
                      <td className="p-4">{fmtDate(s.end_date)}</td>
                      <td className="p-4">{s.status === "attiva" ? <span className="text-[#2f5d3e] font-semibold">Attivo</span> : s.status}</td>
                      <td className="p-4">
                        <button data-testid={`sub-edit-open-${s.id}`}
                          onClick={() => setEditSub({ id: s.id, start_date: (s.start_date || "").slice(0, 10), duration_months: s.duration_months, interventions_per_month: s.frequency || 1, status: s.status })}
                          className="text-xs font-semibold text-[#1e3a5f] hover:underline min-h-[44px]">Modifica</button>
                      </td>
                    </tr>
                  )
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}

export default function Admin() {
  const [tab, setTab] = useState("dashboard");
  const [interventiPreset, setInterventiPreset] = useState(null);
  const [personePreset, setPersonePreset] = useState(null);

  const goTab = (id, opts = null) => {
    setInterventiPreset(id === "interventi" ? opts : null);
    setPersonePreset(id === "persone" ? opts : null);
    setTab(id);
    if (id === "dashboard") {
      setTimeout(() => document.querySelector('[data-testid="admin-notifications"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
    }
    if (id === "contabilita") {
      setTimeout(() => document.getElementById("admin-subscriptions")?.scrollIntoView({ behavior: "smooth", block: "start" }), 450);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="admin-area">
      <div className="mb-6"><BackNav /></div>
      <p className="eyebrow mb-1">Amministrazione</p>
      <h1 className="font-serif text-4xl text-navy mb-8">Pannello di controllo</h1>

      <div className="flex gap-2 overflow-x-auto pb-2 mb-8" data-testid="admin-tabs">
        {TABS.map(([id, label]) => (
          <button
            key={id}
            data-testid={`admin-tab-${id}`}
            onClick={() => setTab(id)}
            className={`px-4 py-2 min-h-[44px] rounded-full text-sm font-semibold whitespace-nowrap transition-colors duration-200 ${
              tab === id ? "bg-[#1e3a5f] text-white" : "bg-white border border-[#e2e8f0] text-body hover:text-navy"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "dashboard" && <AdminDashboard onNavigate={goTab} />}
      {tab === "iscrizioni" && <AdminIscrizioni />}
      {tab === "interventi" && <AdminInterventi preset={interventiPreset} />}
      {tab === "giri" && <AdminGiri />}
      {tab === "ricorrenze" && <AdminRicorrenze />}
      {tab === "cimiteri_req" && <AdminRichiesteCimiteri />}
      {tab === "persone" && <AdminPersone preset={personePreset} />}
      {tab === "catalogo" && <AdminCatalogo />}
      {tab === "contabilita" && <Contabilita />}
    </div>
  );
}
